import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowUpRight, Clock3 } from "lucide-react";
import api from "@/lib/api";

const SESSION_KEY = "eb_trial_banner_dismissed";

function parseISO(s) {
  if (!s) return null;
  try { return new Date(s); } catch { return null; }
}

export default function TrialBanner() {
  const nav = useNavigate();
  const location = useLocation();
  const [sub, setSub] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  // CRITICAL: never render on /kiosk routes
  const onKiosk = location.pathname.startsWith("/kiosk");

  useEffect(() => {
    if (onKiosk) return;
    if (sessionStorage.getItem(SESSION_KEY)) {
      setDismissed(true);
    }
    (async () => {
      try {
        const { data } = await api.get("/subscriptions/me");
        setSub(data);
      } catch { /* not logged in or no sub */ }
    })();
  }, [onKiosk, location.pathname]);

  if (onKiosk || dismissed || !sub) return null;
  if (sub.status !== "trialing") return null;

  const trialEnd = parseISO(sub.trial_ends_at);
  if (!trialEnd) return null;
  const msLeft = trialEnd.getTime() - Date.now();
  const daysLeft = Math.ceil(msLeft / (24 * 60 * 60 * 1000));
  if (daysLeft > 3) return null;

  const severity = daysLeft <= 1 ? "danger" : "orange";
  const colors = severity === "danger"
    ? "bg-danger/10 border-danger/40 text-danger"
    : "bg-orange-100 border-orange-400 text-orange-900";

  const dismiss = () => {
    sessionStorage.setItem(SESSION_KEY, "1");
    setDismissed(true);
  };

  return (
    <div
      className={`rounded-lg border ${colors} px-4 py-3 mb-4 flex items-start gap-3`}
      data-testid="trial-banner"
    >
      <Clock3 className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <div className="flex-1 text-sm">
        Your trial ends in <strong>{daysLeft} day{daysLeft === 1 ? "" : "s"}</strong>. Upgrade to keep EntryBook running.
      </div>
      <button
        onClick={() => nav("/billing?source=trial_banner")}
        data-testid="trial-banner-upgrade-btn"
        className="inline-flex items-center gap-1.5 rounded-md bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold px-3 py-1.5"
      >
        Upgrade <ArrowUpRight className="w-3 h-3" />
      </button>
      <button
        onClick={dismiss}
        data-testid="trial-banner-dismiss"
        className="text-xs text-current/60 hover:text-current"
      >
        Dismiss
      </button>
    </div>
  );
}
